import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnChanges,
	Output,
	PLATFORM_ID,
	SimpleChanges,
	ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { map } from 'rxjs';

import {
	CalendarComponent,
	FlatPickrEvent,
	FlatPickrEventType,
} from '@valk-nx/components/ui-calendar/src/lib/calendar';
import { InputComponent } from '@valk-nx/components/ui-input/src/lib/input';
import { InputInterface } from '@valk-nx/components/ui-input/src/lib/input.interface';
import { LabelComponent } from '@valk-nx/components/ui-label/src/lib/label/label';
import { PopoverModule } from '@valk-nx/components/ui-popover/src/lib/popover.module';
import { ClickedModule } from '@valk-nx/core/lib/directives/clicked/clicked.module';
import { FlatpickrFacade } from '@valk-nx/flatpickr-store/flatpickr.facade';

import { BaseDatePickerComponent } from './base-date-picker';
import { LabelSettings } from './date-picker.interface';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	imports: [
		CalendarComponent,
		ClickedModule,
		CommonModule,
		InputComponent,
		LabelComponent,
		PopoverModule,
	],
	providers: [FlatpickrFacade],
	exportAs: 'vpDatePicker',
	selector: `vp-date-picker`,
	templateUrl: './date-picker.html',
})
export class DatePickerComponent
	extends BaseDatePickerComponent
	implements AfterViewInit, OnChanges
{
	@Input({ required: true }) calendarData: string[];
	@Input({ required: true }) inputConfig: Partial<InputInterface>;
	@Input({ required: true }) labelSettings: LabelSettings;
	@Output() emitDates = new EventEmitter<Date[]>();

	constructor(
		@Inject(PLATFORM_ID) readonly platformId: string,
		override readonly flatpickrFacade: FlatpickrFacade,
	) {
		super(flatpickrFacade);
		this.prettyDate$ = flatpickrFacade.selectSelectedDates$.pipe(
			takeUntilDestroyed(),
			map((selectedDates: Date[]) => {
				if (selectedDates.length > 0) {
					return this.formatDate(selectedDates[0]);
				}
				return '';
			}),
		);
	}

	ngAfterViewInit() {
		if (isPlatformBrowser(this.platformId)) {
			this.masterInputConfig = {
				...this.masterInputConfig,
				...this.inputConfig,
			};
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		if (isPlatformBrowser(this.platformId)) {
			if (
				changes['config']?.currentValue.defaultDate !==
				changes['config']?.previousValue?.defaultDate
			) {
				const defaultDate = changes['config'].currentValue.defaultDate;

				this.isDateInThePast(defaultDate);

				if (defaultDate) {
					this.flatpickrFacade.setDatesAndUpdateInCalendar([
						defaultDate,
					]);
					this.masterInputConfig = {
						...this.masterInputConfig,
						hasError: false,
					};
				}
				if (defaultDate === undefined) {
					this.masterInputConfig = {
						...this.masterInputConfig,
						hasError: true,
					};
				}
			}
		}
		if (changes['inputConfig']) {
			this.masterInputConfig = {
				...this.masterInputConfig,
				...changes['inputConfig'].currentValue,
			};
		}
		if (changes['disabled']) {
			this.masterInputConfig.disabled = this.disabled;
		}
	}

	onEmitDates(dates: Date[]) {
		this.emitDates.emit(dates);
	}

	onDatePickerEmitter(event: FlatPickrEvent) {
		if (event.type === FlatPickrEventType.OnReady) {
			this.flatpickrFacade.setDates(event.selectedDates);
		} else if (event.type === FlatPickrEventType.OnValueUpdate) {
			this.flatpickrFacade.setDates(event.selectedDates);
			this.emitDates.emit(event.selectedDates);
			this.popoverStateChanged(false);
		}
	}
}
